/**
 * Sample values for an element, and the title a card would READ as.
 *
 * The display-template editor has always previewed a template against
 * made-up values so an author sees the transforms acting on something
 * realistic ("Manufacturer + Truncate(5)" → "Manuf…"). The card home shows
 * the same thing one step later — the card's badge wearing the title its
 * items get — so the sample machinery lives here, once, and both surfaces
 * read the same words.
 *
 * A sample is always a value of the element's OWN shape (an E.164 number, a
 * measurement of the element's measure, an ISO instant for a date), because
 * the formatters that turn it into a title are the real ones.
 */
import { buildItemDisplayValue } from './displayTemplate';
import { isUnitOf, toBase } from './units';
import { defaultUnitFor } from './measurements';
import { defaultCodeOf, toMinor } from './currency';
import { normalizeColor } from './elementFormat/color';
import { IDENTITY_HUES } from '../config/brand';

/** An element's human label, whichever shape the caller's element carries. */
const labelText = (element) => {
	const label = element?.label;
	if (typeof label === 'string') return label;
	if (label && typeof label === 'object') return label.singular || label.plural || '';
	return '';
};

// The measurement sample is an infant's weight in lb-oz when the element's
// measure has that unit (the case the display rules exist for), else 8.4375
// of the element's default unit — always a value of the element's measure so
// its own rules and a `unit(...)` formatter act on it in the preview.
export const measurementSample = (element) => {
	const options = element?.options || {};
	const unit = isUnitOf('lb-oz', options.measure) ? 'lb-oz' : defaultUnitFor(options);
	if (!unit) return '';
	return { value: 8.4375, unit, base: toBase(8.4375, unit) };
};

// The currency sample is 1,234.50 of the element's default currency (JPY
// reads ¥1,235, BHD 1.235 — its own decimals), stored as minor units.
export const currencySample = (element) => {
	const code = defaultCodeOf(element?.options || {}) || 'USD';
	const amount = toMinor(1234.5, code, 'round');
	return amount === null ? '' : { amount, currency: code };
};

// A color sample: an identity hue (user data, never a theme color), as stored.
const sampleColor = () => normalizeColor((IDENTITY_HUES.find(hue => hue.key === 'blue') || IDENTITY_HUES[0]).value).value;

/** One believable value for one element. `sampleNow` is an ISO instant. */
export const sampleValueFor = (element, sampleNow) => {
	const dataType = element?.dataType;
	if (['number', 'decimal', 'integer'].includes(dataType)) return 1234.5;
	if (dataType === 'measurement') return measurementSample(element);
	if (dataType === 'currency') return currencySample(element);
	if (dataType === 'rating') return 4;
	// The TRUE state, so the preview shows the element's own "yes" word.
	if (dataType === 'boolean') return true;
	// The three text-adjacent types show a value of their own SHAPE, so the
	// preview exercises the real formatter: an E.164 number renders in the
	// element's dialling style, a location as the address it holds.
	if (dataType === 'phone') return '+12025550123';
	if (dataType === 'email') return 'sam@example.com';
	if (dataType === 'location') return { address: '1 Market St, San Francisco' };
	// A color shows the hex a title prints: the identity hue Blue, in the
	// stored spelling (the sample is text, never painted).
	if (dataType === 'color') return sampleColor();
	if (['date', 'date-time', 'time'].includes(dataType)) return sampleNow;
	return labelText(element) || element?.name || '';
};

/** `{ [element name]: sample }` for a list of element definitions. */
export const sampleElementValues = (elements = [], sampleNow) => Object.fromEntries(
	(elements || [])
		.filter(element => element?.name)
		.map(element => [element.name, sampleValueFor(element, sampleNow)])
);

/**
 * What an item of this card would be called: the card's display template
 * rendered over sample values, or — for a card with no template — the
 * platform's default composition ({first field} · {short date}), which is
 * exactly what `buildItemDisplayValue` falls back to.
 *
 * `name` is the last resort (a card with no elements at all).
 */
export function sampleDisplayValue({ template, elements = [], sampleNow, name }) {
	const defs = (elements || []).filter(element => element?.name);
	const now = sampleNow || new Date().toISOString();
	return buildItemDisplayValue({
		template,
		elements: sampleElementValues(defs, now),
		fallbackFields: defs.map(element => element.name),
		elementDefinitions: defs,
		name,
	});
}
